// Серверный список разделов (README §10.3, docs/api-ui-v3.md §6): свои
// разделы профиля и оверлеи встроенных. Отсюда — вкладки опубликованных
// разделов в верхней полосе, вкладки черновиков и метка «черновик».

import { useEffect } from 'react'
import { create } from 'zustand'
import { useAppStore } from '../../store/appStore'
import type { DraftTab, Section } from '../../store/appStore'
import { http } from './http'
import type { SectionModel, SectionSummary, UikitPayload, VersionRow } from './types'

const POLL_MS = 10_000
/** sectionId → sessionId беседы-сборщика: вкладка черновика после перезагрузки
 *  снова ведёт в свою беседу. */
const CHAT_KEY = 'abx-section-chats'

export function readChats(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem(CHAT_KEY) || '{}') as unknown
    return raw && typeof raw === 'object' ? (raw as Record<string, string>) : {}
  } catch {
    return {}
  }
}

export function rememberChat(sectionId: string, sessionId: string | null): void {
  const all = readChats()
  if (sessionId) all[sectionId] = sessionId
  else delete all[sectionId]
  try {
    localStorage.setItem(CHAT_KEY, JSON.stringify(all))
  } catch {
    // приватный режим — после перезагрузки вкладка откроет предпросмотр
  }
}

export const sectionsApi = {
  list: () => http<{ sections: SectionSummary[] }>('/sections'),
  uikit: () => http<UikitPayload>('/sections/uikit'),
  full: (id: string) => http<SectionSummary & { draft: SectionModel | null; section: SectionModel | null }>(`/sections/${id}`),
  draft: (id: string) => http<SectionModel>(`/sections/${id}/draft`),
  putDraft: (id: string, model: SectionModel) =>
    http<SectionSummary>(`/sections/${id}/draft`, { method: 'PUT', body: JSON.stringify(model) }),
  accept: (id: string) => http<SectionSummary & { version: string }>(`/sections/${id}/accept`, { method: 'POST' }),
  discard: (id: string) => http<SectionSummary | { id: string; deleted: true }>(`/sections/${id}/discard`, { method: 'POST' }),
  versions: (id: string) => http<{ versions: VersionRow[] }>(`/sections/${id}/versions`),
  rollback: (id: string, version: string) =>
    http<SectionSummary & { version: string }>(`/sections/${id}/rollback`, { method: 'POST', body: JSON.stringify({ version }) }),
  remove: (id: string) => http<unknown>(`/sections/${id}`, { method: 'DELETE' }),
  builderToken: (id: string) =>
    http<{ token: string; url: string; expiresAt: number }>(`/sections/${id}/builder-token`, { method: 'POST' }),
  data: (id: string, source: string, draft: boolean) =>
    http<{ rows: Record<string, unknown>[] | null; text: string | null; data: unknown }>(
      `/sections/${id}/data/${encodeURIComponent(source)}?draft=${draft}`,
    ),
}

/** id раздела на сервере для раздела оболочки: `custom:x` → x, `draft:x` → x,
 *  встроенные — как есть. Кабинет и База знаний не правятся. */
export function serverId(section: Section | string): string | null {
  if (section.startsWith('custom:') || section.startsWith('draft:')) return section.slice(section.indexOf(':') + 1)
  if (['chats', 'feed', 'flow', 'tracker', 'notes', 'messengers', 'library'].includes(section)) return section
  return null
}

interface SectionsState {
  list: SectionSummary[]
  loaded: boolean
  refresh: () => Promise<void>
}

export const useSections = create<SectionsState>()((set) => ({
  list: [],
  loaded: false,
  refresh: async () => {
    try {
      const { sections } = await sectionsApi.list()
      set({ list: sections, loaded: true })
      syncDraftTabs(sections)
    } catch {
      // сеть моргнула — оставим прежний список
    }
  },
}))

/** Вкладки черновиков новых разделов = серверные черновики без публикации
 *  плюс только что начатые в этой вкладке браузера (агент ещё ничего не записал). */
function syncDraftTabs(sections: SectionSummary[]): void {
  const app = useAppStore.getState()
  const chats = readChats()
  const pending = sections.filter((s) => !s.builtin && s.hasDraft && !s.published)
  let drafts: DraftTab[] = app.drafts.map((tab) => {
    const row = pending.find((s) => `draft:${s.id}` === tab.id)
    return row?.draftTitle && row.draftTitle !== tab.title ? { ...tab, title: row.draftTitle } : tab
  })
  // опубликованный за это время раздел — больше не черновик-вкладка
  drafts = drafts.filter((tab) => !sections.some((s) => `draft:${s.id}` === tab.id && s.published))
  for (const row of pending) {
    const id = `draft:${row.id}` as const
    if (!drafts.some((tab) => tab.id === id)) {
      drafts.push({ id, title: row.draftTitle || 'новый раздел', sessionId: chats[row.id] ?? null })
    }
  }
  const same = drafts.length === app.drafts.length && drafts.every((d, i) => d === app.drafts[i])
  if (!same) useAppStore.setState({ drafts })
}

let timer: number | null = null
let users = 0

/** Подписка на серверный список с опросом, пока смонтирован хоть один потребитель. */
export function useSectionsPoll(): SectionsState['list'] {
  const list = useSections((s) => s.list)
  // эффект без зависимостей от list: один таймер на всю панель
  useSectionsTimer()
  return list
}


function useSectionsTimer(): void {
  useEffect(() => {
    users += 1
    if (timer === null) {
      void useSections.getState().refresh()
      timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') void useSections.getState().refresh()
      }, POLL_MS)
    }
    return () => {
      users -= 1
      if (users === 0 && timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
  }, [])
}

export function summaryOf(id: string): SectionSummary | undefined {
  return useSections.getState().list.find((s) => s.id === id)
}
